import React, { useEffect } from 'react';
import { TTRestaurantProfile } from '../../interfaces/restaurant';
import TrackingContext from './TrackingContext';
import store from '../../store';
import ReactPixel, { AdvancedMatching } from 'react-facebook-pixel';
import ReactGA from 'react-ga4';
import SHA256 from 'crypto-js/sha256';
import { useSelector } from 'react-redux';
import { RootState } from '../../rootReducer';

interface TrackingProviderProps {
  children: React.ReactNode;
  profiles: TTRestaurantProfile[];
}

// react-facebook-pixel's bundled types declare every AdvancedMatching field as
// required and omit external_id, but Facebook treats all matching params as
// optional and does support external_id. Model the real shape and assert at the
// call boundary.
type PixelMatching = Partial<AdvancedMatching> & { external_id?: string };

const TrackingProvider = ({ children }: TrackingProviderProps) => {
  const profiles = useSelector<RootState, TTRestaurantProfile[]>(
    state => state.restaurant.all_profiles ?? [],
  );
  useEffect(() => {
    profiles.forEach(profile => {
      const {
        facebook_pixel_id,
        google_analytics_id,
        google_adwords_id,
      } = profile;
      if (facebook_pixel_id) {
        const options = {
          autoConfig: true, // set pixel's autoConfig. More info: https://developers.facebook.com/docs/facebook-pixel/advanced/
          debug: true, // enable logs
        };
        if (store.getState().auth.userProfile) {
          const _user = store.getState().auth.userProfile;
          let advancedMatching: PixelMatching = {
            em: SHA256(_user.email).toString(),
            fn: SHA256(_user.first_name ?? '').toString(),
            ln: SHA256(_user.last_name ?? '').toString(),
            ph: SHA256(_user.phone_number ?? '').toString(),
            external_id: SHA256(`user_${_user.id}`).toString(),
          };
          ReactPixel.init(
            facebook_pixel_id,
            advancedMatching as AdvancedMatching,
            options,
          );
          ReactPixel.pageView();
        } else {
          console.log('pixel is setup');
          ReactPixel.init(facebook_pixel_id, {} as AdvancedMatching, options);
        }
      }
      if (google_adwords_id && google_analytics_id) {
        ReactGA.initialize([
          {
            trackingId: google_adwords_id,
            gtagOptions: { send_page_view: false },
          },
          {
            trackingId: google_analytics_id,
            gtagOptions: { send_page_view: false },
          },
        ]);
        ReactGA.event('page_view', {
          page_title: `${profile.name} - Landing Page`,
          page_url: `${window.location}`,
        });
      } else if (google_analytics_id) {
        // Insert Google AdWords
        ReactGA.initialize(google_analytics_id, {
          gtagOptions: {
            send_page_view: false,
          },
        });
        ReactGA.event('page_view', {
          page_title: `${profile.name} - Landing Page`,
          page_url: `${window.location}`,
        });
      } else if (google_adwords_id) {
        ReactGA.initialize(google_adwords_id, {
          gtagOptions: { send_page_view: false },
        });
        ReactGA.event('page_view', {
          page_title: `${profile.name} - Landing Page`,
          page_url: `${window.location}`,
        });
      }
    });
  }, [profiles]);

  // Provide a function to track events
  const trackEvent = (
    eventName: string,
    fbParams: any,
    gaParams: any,
    applicableMerchants: number[],
  ) => {
    profiles.forEach(p => {
      if (applicableMerchants.includes(p.restaurant_id)) {
        if (p.facebook_pixel_id) {
          ReactPixel.trackSingle(p.facebook_pixel_id, eventName, fbParams);
        }
        if (p.google_adwords_id && p.google_analytics_id) {
          ReactGA.gtag('event', eventName, {
            ...gaParams,
            send_to: p.google_adwords_id,
          });
          ReactGA.gtag('event', eventName, {
            ...gaParams,
            send_to: p.google_analytics_id,
          });
        } else if (p.google_adwords_id) {
          ReactGA.gtag('event', eventName, {
            ...gaParams,
            send_to: p.google_adwords_id,
          });
        } else if (p.google_analytics_id) {
          ReactGA.gtag('event', eventName, {
            ...gaParams,
            send_to: p.google_analytics_id,
          });
        }
      }
    });
  };

  const trackPageView = (params: any) => {
    console.log('traking page view!');
    ReactPixel.track('PageView');
    ReactGA.send({
      hitType: 'pageview',
      page: params['locations'],
      title: params['title'],
    });
  };

  const trackFBEvent = (
    eventName: string,
    params: any,
    isCustom: boolean,
    applicableMerchants: number[],
  ) => {
    profiles.forEach(p => {
      if (applicableMerchants.includes(p.restaurant_id)) {
        if (p.facebook_pixel_id) {
          isCustom
            ? ReactPixel.trackSingle(p.facebook_pixel_id, eventName, params)
            : ReactPixel.trackSingleCustom(
                p.facebook_pixel_id,
                eventName,
                params,
              );
        }
      }
    });
  };

  const trackGAEvent = (
    eventName: string,
    params: any,
    applicableMerchants: number[],
  ) => {
    profiles.forEach(p => {
      if (applicableMerchants.includes(p.restaurant_id)) {
        if (p.google_adwords_id && p.google_analytics_id) {
          ReactGA.gtag('event', eventName, {
            ...params,
            send_to: p.google_adwords_id,
          });
          ReactGA.gtag('event', eventName, {
            ...params,
            send_to: p.google_analytics_id,
          });
        } else if (p.google_adwords_id) {
          ReactGA.gtag('event', eventName, {
            ...params,
            send_to: p.google_adwords_id,
          });
        } else if (p.google_analytics_id) {
          ReactGA.gtag('event', eventName, {
            ...params,
            send_to: p.google_analytics_id,
          });
        }
      }
    });
  };

  return (
    <TrackingContext.Provider
      value={{ trackEvent, trackPageView, trackFBEvent, trackGAEvent }}
    >
      {children}
    </TrackingContext.Provider>
  );
};

export default TrackingProvider;
